import React from 'react';
import * as Store from '@apply/store';
import { DataRow } from './DataRow';
import {
    Accordion,
    buildTypeString,
    buildRentTypeString,
    preferredTerms,
    preferredTermString,
    ShowIf,
    formatDate,
    formatValue,
    PropertySubheader,
} from '@apply/ui';
import { NumericFormat } from 'react-number-format';
import { LoanType } from '@apply/constants';

export interface RentalPropertyReviewProps {
    property: Store.PropertiesStore.Types.Property;
    index: number;
    apiId?: string;
    formCompleted: boolean;
    loanType?: LoanType;
    token: string;
    printMode: boolean;
    collapsable: boolean;
}

export const RentalPropertyReview: React.FC<RentalPropertyReviewProps> = ({
    property,
    index,
    apiId,
    formCompleted,
    loanType,
    token,
    printMode,
    collapsable,
}) => {
    const provideLaterAdvisory = Store.PropertiesStore.Types.constructReviewFormsAdvisoryMessagePrefix(loanType, property);

    const isPurchaseDateLessThanTwoYears = (): boolean => {
        if (!property?.rental?.purchaseDate) return false;

        const purchaseYearMonthDay = property?.rental?.purchaseDate.replace('-', '').replace('-', '');

        const twoYearsBeforeToday = new Date();
        twoYearsBeforeToday.setFullYear(twoYearsBeforeToday.getFullYear() - 2);

        const twoYearsBeforeYearMonthDay = `${twoYearsBeforeToday.getFullYear()}${(twoYearsBeforeToday.getMonth() + 1)
            .toString()
            .padStart(2, '0')}${twoYearsBeforeToday.getDate().toString().padStart(2, '0')}`;
        return purchaseYearMonthDay > twoYearsBeforeYearMonthDay; // i.e. '20220619' > '20220630' (YYYYMMDD)
    };

    return (
        <>
            <div className='scrollable-content'>
                <div
                    className='accordion gray-adjust'
                    data-accordion=''
                    data-allow-all-closed='true'
                    data-multi-expand='true'
                >
                    <Accordion
                        title={
                            <PropertySubheader
                                index={index}
                                property={property}
                                apiId={apiId}
                                formCompleted={formCompleted}
                                token={token}
                                printMode={printMode}
                            />
                        }
                        isCollapsable={collapsable}
                    >
                        <DataRow value={property?.address}>Property address</DataRow>

                        <ShowIf showIf={loanType === LoanType.BuildRent}>
                            <DataRow value={property?.nonRental?.legalDescription}>Legal description</DataRow>
                            <DataRow value={buildRentTypeString(property.rental?.buildRentTypeId)}>
                                Will new square footage be built?
                            </DataRow>
                            <DataRow value={property?.nonRental?.landSize}>Land size</DataRow>
                            <DataRow value={buildTypeString(property.rental?.buildType)}>
                                Is it a Site Built or Manufactured home?
                            </DataRow>
                            <DataRow value={property?.nonRental?.generalContractorWillWork}>
                                {' '}
                                Will a third-party General Contractor work on this?
                            </DataRow>
                            <ShowIf showIf={property?.nonRental?.generalContractorWillWork}>
                                <DataRow value={property?.nonRental?.generalContractorName}>G.C. Company Name</DataRow>
                                <DataRow value={property?.nonRental?.generalContractorResume}>G.C. Resume</DataRow>
                            </ShowIf>

                            <ShowIf showIf={!property?.nonRental?.generalContractorWillWork}>
                                <DataRow value={property?.nonRental?.name}>Contact name</DataRow>
                                <DataRow value={property?.nonRental?.email}>Contact email</DataRow>
                                <DataRow value={property?.nonRental?.phone}>Contact phone</DataRow>
                            </ShowIf>

                            <DataRow value={property?.nonRental?.budget}>Budget</DataRow>
                            <DataRow value={property?.nonRental?.plans}>Plans</DataRow>
                            <DataRow value={property?.nonRental?.specSheet}>Spec sheet</DataRow>
                            <DataRow value={property?.nonRental?.survey}>Survey</DataRow>
                        </ShowIf>

                        <ShowIf showIf={loanType === LoanType.Rental}>
                            <DataRow value={property?.rental?.yearBuilt}>Year Built</DataRow>
                            <DataRow value={buildTypeString(property.rental?.buildType)}>
                                Is it a Site Built or Manufactured home?
                            </DataRow>
                            <DataRow value={property.rental?.isAquisitionFinancing}>Is funding required at closing?</DataRow>

                            <ShowIf showIf={property?.rental?.isAquisitionFinancing === true}>
                                <DataRow value={property?.rental?.purchaseContract}>Purchase contract</DataRow>
                                <DataRow value={property?.rental?.moreThan10kExpenditures}>
                                    In the first 6 months of ownership are over $20,000 of capital expenditures planned?
                                </DataRow>
                                <ShowIf showIf={property?.rental?.moreThan10kExpenditures === true}>
                                    <DataRow value={property?.rental?.capitalExpenditures}>Capital Expenditures</DataRow>
                                </ShowIf>
                            </ShowIf>
                            <ShowIf showIf={property?.rental?.isAquisitionFinancing === false}>
                                <DataRow
                                    value={property?.rental?.currentDebt || 0}
                                    format={'currency-short'}
                                >
                                    Current debt on property (enter $0 if no debt you owe)
                                </DataRow>
                                <ShowIf
                                    showIf={
                                        property.rental?.currentDebt?.toString() === '0' ||
                                        property.rental?.currentDebt?.toString() === ''
                                    }
                                >
                                    <DataRow value={property?.rental?.isLoanProceedsUsedPropertyImprovements}>
                                        Will the loan proceeds be used to improve the property?
                                    </DataRow>
                                </ShowIf>
                                <DataRow value={formatDate(new Date(property?.rental?.purchaseDate))}>Date of Purchase</DataRow>
                                <ShowIf showIf={isPurchaseDateLessThanTwoYears()}>
                                    <DataRow value={property?.rental?.purchaseContract}>Purchase contract</DataRow>
                                    <DataRow value={property?.rental?.moreThan10kExpenditures}>
                                        Have you done (or within six months will you do) more then $20,000 of capital
                                        expenditures?
                                    </DataRow>
                                    <ShowIf showIf={property?.rental?.moreThan10kExpenditures === true}>
                                        <DataRow value={property?.rental?.capitalExpenditures}>Capital Expenditures</DataRow>
                                    </ShowIf>
                                </ShowIf>
                                <ShowIf showIf={property.rental?.purchaseDate && !isPurchaseDateLessThanTwoYears()}>
                                    <DataRow value={property?.rental?.moreThan10kExpenditures}>
                                        In the first 6 months of ownership are over $20,000 of capital expenditures planned?
                                    </DataRow>
                                    <ShowIf showIf={property?.rental?.moreThan10kExpenditures === true}>
                                        <DataRow value={property?.rental?.capitalExpenditures}>Capital Expenditures</DataRow>
                                    </ShowIf>
                                </ShowIf>
                            </ShowIf>
                        </ShowIf>

                        <ShowIf showIf={loanType === LoanType.BuildRent}>
                            <DataRow value={property?.rental?.purchasedLessThan2YearsAgo}>
                                Did you purchase the property 2 years ago or less?
                            </DataRow>
                            <ShowIf showIf={property?.rental?.purchasedLessThan2YearsAgo}>
                                <DataRow value={property?.rental?.purchaseContract}>Purchase contract</DataRow>
                            </ShowIf>
                            <ShowIf
                                showIf={loanType !== LoanType.BuildRent && property?.rental?.purchasedLessThan2YearsAgo === true}
                            >
                                <DataRow value={property?.rental?.moreThan10kExpenditures && loanType !== LoanType.BuildRent}>
                                    Within 6 months of purchase, do you plan over $20,000 of capital expenditures?
                                </DataRow>
                            </ShowIf>
                            <ShowIf showIf={property?.rental?.moreThan10kExpenditures}>
                                <DataRow value={property?.rental?.capitalExpenditures}>Capital Expenditures</DataRow>
                            </ShowIf>
                            <DataRow
                                value={property?.rental?.currentDebt || 0}
                                format={'currency-short'}
                            >
                                Current debt on property (approximate)
                            </DataRow>
                        </ShowIf>

                        <ShowIf showIf={!!provideLaterAdvisory}>
                            <div className='row'>
                                <div
                                    style={{
                                        paddingRight: '50px',
                                        paddingLeft: '50px',
                                    }}
                                >
                                    <strong>
                                        <em>NOTE:</em>
                                    </strong>
                                    &nbsp; {provideLaterAdvisory} are/is needed to process your application and order a valuation.
                                    Please submit ASAP via&nbsp;
                                    <a href='mailto:team@builderfinance.com'>team@builderfinance.com</a>
                                    &nbsp; or&nbsp;
                                    <a
                                        href='https://fileshare.bfiweb.com'
                                        target='_blank'
                                        rel='noreferrer'
                                    >
                                        https://fileshare.bfiweb.com
                                    </a>
                                </div>
                            </div>
                        </ShowIf>

                        <DataRow
                            value={property?.rental?.targetLoanRequest}
                            format={'currency-short'}
                        >
                            Target Loan Request
                        </DataRow>
                        <DataRow value={preferredTermString(property?.rental?.preferredTermType as preferredTerms)}>
                            Preferred term
                        </DataRow>
                        <DataRow value={property?.structureType}>Structure Type</DataRow>
                        <ShowIf showIf={!!property?.rental?.numberRentalUnits}>
                            <DataRow value={property?.rental?.numberRentalUnits}>Rental units</DataRow>
                            <ShowIf showIf={property.structureType === '5+ Unit'}>
                                <DataRow value={property.rental?.unitsIncomeRestricted}>
                                    Units subject to income restricted federal, state, or local housing program
                                </DataRow>
                            </ShowIf>
                        </ShowIf>

                        <h4>{loanType === LoanType.BuildRent ? 'Rental Information' : 'Unit information'}</h4>

                        <div className='row columns table'>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Unit</th>
                                        <ShowIf showIf={loanType !== LoanType.BuildRent}>
                                            <th>Occupied</th>
                                        </ShowIf>
                                        <th>Annual Rent</th>
                                        <th>Square Feet</th>
                                        <th>Bedrooms</th>
                                        <th>Bathrooms</th>
                                        <th>Garage Bays</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {property?.rentalUnits?.map((unit, index) => {
                                        return (
                                            <tr key={unit.guid}>
                                                <td className='nowrap primary'>Unit {index + 1}</td>

                                                <ShowIf showIf={loanType !== LoanType.BuildRent}>
                                                    <td className='nowrap'>{formatValue(unit.occupied)}</td>
                                                </ShowIf>
                                                <td>
                                                    <NumericFormat
                                                        value={unit.rent}
                                                        displayType={'text'}
                                                        decimalScale={2}
                                                        thousandSeparator={true}
                                                        prefix={'$'}
                                                    />
                                                </td>
                                                <td>
                                                    <NumericFormat
                                                        value={unit.squareFeet}
                                                        displayType={'text'}
                                                        decimalScale={2}
                                                        thousandSeparator={true}
                                                    />
                                                </td>
                                                <td>{formatValue(unit.bedrooms)}</td>
                                                <td>{formatValue(unit.bathrooms)}</td>
                                                <td>{formatValue(unit.garageBays || 0)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td
                                            className='nowrap primary'
                                            colSpan={loanType !== LoanType.BuildRent ? 2 : 1}
                                        >
                                            Total
                                        </td>
                                        <td>
                                            <NumericFormat
                                                value={property?.rentalUnits?.reduce(
                                                    (subtotal, current) => subtotal + current.rent,
                                                    0
                                                )}
                                                displayType={'text'}
                                                decimalScale={2}
                                                thousandSeparator={true}
                                                prefix={'$'}
                                            />
                                        </td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </Accordion>
                </div>
            </div>
        </>
    );
};
